/**
 * The pitched half of the library — the port of `MelodicLibrary.swift`: twelve instruments
 * rendered chromatically so they can be played across a pad grid, and five chord qualities
 * rendered in three keys so a beat sounds like music the moment the second pad is hit.
 *
 * Every patch here is built around one idea: the *filter* envelope and the *pitch* envelope
 * are what separate one instrument from another, far more than the oscillator is. A saw
 * through a fast, resonant, key-tracked low-pass is a pluck; the same saw through a slow one
 * is a string section. The oscillator choices are deliberately plain so the envelopes do the
 * work and the results stay in tune.
 *
 * **No layer is ever tuned below the root.** A sub-octave layer sounds fatter, but it moves
 * the fundamental down twelve semitones, and then the sample's name, its `root:` tag and
 * every tuner disagree with what a listener hears. Weight is added with harmonics above the
 * root instead — +12, +19 and +24 are the second, third and fourth harmonics, so they
 * thicken the tone without inventing a new fundamental.
 */

import { makeEffectChain, makeEffectNode, type PadColor } from '../../model/types';
import { applyEffectChain, applyEffectNode, type RenderedAudio } from '../offline-render';
import {
  makeSynthEnvelope,
  makeSynthFilter,
  makeSynthLFO,
  makeSynthLayer,
  makeSynthPatch,
  makeSynthPitchEnvelope,
  renderPatch,
  type SynthPatch,
} from '../synth';
import {
  applyFormantBank,
  chromatic,
  finishToRMS,
  makeLibraryEntry,
  noteFileToken,
  notePitchClass,
  type LibraryEntry,
  type LibraryRenderContext,
  type Vowel,
} from './entry';
import { librarySeed } from './identity';
import { noteFrequency, noteName, noteSlug } from './notes';
import { mixInto } from './renderer';

/**
 * Instruments are rendered across a full octave *inclusive* — thirteen notes — so a 4 × 4
 * grid holds a whole octave with three slots left for a transpose or a variation.
 */
export const NOTES_PER_INSTRUMENT = 13;

export type Instrument =
  | 'electricPiano'
  | 'warmPad'
  | 'pluck'
  | 'bell'
  | 'marimba'
  | 'organ'
  | 'sawLead'
  | 'squareLead'
  | 'brassStab'
  | 'stringSwell'
  | 'voxAh'
  | 'voxOh';

export const INSTRUMENTS: readonly Instrument[] = [
  'electricPiano',
  'warmPad',
  'pluck',
  'bell',
  'marimba',
  'organ',
  'sawLead',
  'squareLead',
  'brassStab',
  'stringSwell',
  'voxAh',
  'voxOh',
];

export function instrumentDisplayName(instrument: Instrument): string {
  switch (instrument) {
    case 'electricPiano':
      return 'EP Key';
    case 'warmPad':
      return 'Warm Pad';
    case 'pluck':
      return 'Pluck';
    case 'bell':
      return 'Bell';
    case 'marimba':
      return 'Marimba';
    case 'organ':
      return 'Organ';
    case 'sawLead':
      return 'Saw Lead';
    case 'squareLead':
      return 'Square Lead';
    case 'brassStab':
      return 'Brass Stab';
    case 'stringSwell':
      return 'String Swell';
    case 'voxAh':
      return 'Synth Vox Ah';
    case 'voxOh':
      return 'Synth Vox Oh';
  }
}

/**
 * File-name stem. Lower case and hyphenated, so the media folder sorts by instrument and then
 * by note without any extra metadata.
 */
function instrumentToken(instrument: Instrument): string {
  switch (instrument) {
    case 'electricPiano':
      return 'ep-key';
    case 'warmPad':
      return 'warm-pad';
    case 'pluck':
      return 'pluck';
    case 'bell':
      return 'bell';
    case 'marimba':
      return 'marimba';
    case 'organ':
      return 'organ';
    case 'sawLead':
      return 'saw-lead';
    case 'squareLead':
      return 'square-lead';
    case 'brassStab':
      return 'brass-stab';
    case 'stringSwell':
      return 'string-swell';
    case 'voxAh':
      return 'synth-vox-ah';
    case 'voxOh':
      return 'synth-vox-oh';
  }
}

function instrumentCaption(instrument: Instrument): string {
  switch (instrument) {
    case 'electricPiano':
      return 'Electric-piano key. A sine body with a bell-like tine over it, opened by a short filter sweep on the attack.';
    case 'warmPad':
      return 'Warm pad. Four detuned saws behind a slow filter swell that keeps drifting under a gentle LFO.';
    case 'pluck':
      return 'Pluck. A resonant low-pass slammed shut in a tenth of a second, with a short echo behind it.';
    case 'bell':
      return 'Bell. Three sine partials an octave and a twelfth apart, struck with a fast upward pitch blip.';
    case 'marimba':
      return 'Marimba-style mallet. A sine with its fourth harmonic and a noise click for the beater.';
    case 'organ':
      return 'Organ. Three drawbar sines, lightly overdriven, pulsing with a rotary-style tremolo.';
    case 'sawLead':
      return 'Saw lead. Three detuned saws through a resonant filter that snaps down and holds.';
    case 'squareLead':
      return 'Square lead. Hollow and cutting, with a saw underneath for body.';
    case 'brassStab':
      return 'Brass-style stab. The filter opens across the first thirty milliseconds and the pitch scoops up into the note.';
    case 'stringSwell':
      return 'String-style swell. Five detuned saws fading in under a slow filter, with a long reverb tail.';
    case 'voxAh':
      return 'Synthetic “ah”. Not a recording: a saw through a three-band vocal-tract filter, with a light vibrato.';
    case 'voxOh':
      return 'Synthetic “oh”. The same vocal-tract filter moved down two semitones by the formant shifter for a larger throat.';
  }
}

/**
 * Colour is assigned by family, never per sound. Every melodic one-shot is violet and every
 * synthetic vocal is magenta, so the board reads as two groups rather than as twelve
 * unrelated hues.
 */
function instrumentColor(instrument: Instrument): PadColor {
  return instrument === 'voxAh' || instrument === 'voxOh' ? 'magenta' : 'violet';
}

function instrumentSymbol(instrument: Instrument): string {
  switch (instrument) {
    case 'electricPiano':
      return 'pianokeys';
    case 'warmPad':
      return 'cloud.fill';
    case 'pluck':
      return 'guitars.fill';
    case 'bell':
      return 'bell.fill';
    case 'marimba':
      return 'square.grid.3x3.fill';
    case 'organ':
      return 'pianokeys.inverse';
    case 'sawLead':
      return 'bolt.horizontal.fill';
    case 'squareLead':
      return 'square.fill';
    case 'brassStab':
      return 'megaphone.fill';
    case 'stringSwell':
      return 'waveform.path';
    case 'voxAh':
    case 'voxOh':
      return 'mouth.fill';
  }
}

function instrumentTags(instrument: Instrument): string[] {
  switch (instrument) {
    case 'electricPiano':
      return ['melodic', 'keys', 'electric piano'];
    case 'warmPad':
      return ['melodic', 'pad', 'ambient'];
    case 'pluck':
      return ['melodic', 'pluck', 'synth'];
    case 'bell':
      return ['melodic', 'bell', 'mallet'];
    case 'marimba':
      return ['melodic', 'mallet', 'marimba'];
    case 'organ':
      return ['melodic', 'keys', 'organ'];
    case 'sawLead':
      return ['melodic', 'lead', 'saw'];
    case 'squareLead':
      return ['melodic', 'lead', 'square'];
    case 'brassStab':
      return ['melodic', 'stab', 'brass'];
    case 'stringSwell':
      return ['melodic', 'strings', 'swell'];
    case 'voxAh':
    case 'voxOh':
      return ['melodic', 'synthetic vocal', 'pad'];
  }
}

/**
 * Lowest note rendered, as a MIDI number. Chosen per instrument so the octave sits where the
 * instrument is actually played: bells live high, pads and organs low.
 */
function instrumentLowestNote(instrument: Instrument): number {
  switch (instrument) {
    case 'electricPiano':
    case 'warmPad':
    case 'organ':
    case 'brassStab':
    case 'stringSwell':
    case 'voxAh':
    case 'voxOh':
      return 48; // C3
    case 'pluck':
    case 'marimba':
    case 'sawLead':
    case 'squareLead':
      return 60; // C4
    case 'bell':
      return 72; // C5
  }
}

/**
 * Loudness target in dBFS, measured as active RMS. Sustained material sits a little louder
 * than percussive material at the same peak, which is why these differ: matching the *peaks*
 * of a pad and a mallet leaves the mallet sounding half as loud.
 */
function instrumentTargetRMSDB(instrument: Instrument): number {
  switch (instrument) {
    case 'warmPad':
    case 'stringSwell':
    case 'organ':
    case 'voxAh':
    case 'voxOh':
      return -17;
    case 'electricPiano':
    case 'sawLead':
    case 'squareLead':
    case 'brassStab':
      return -18;
    case 'pluck':
    case 'bell':
    case 'marimba':
      return -19;
  }
}

/** Set for the synthetic vocals: which vowel the formant bank is tuned to. */
function instrumentVowel(instrument: Instrument): Vowel | null {
  if (instrument === 'voxAh') return 'ah';
  if (instrument === 'voxOh') return 'oh';
  return null;
}

/**
 * Semitones the finished vocal is pushed through the formant shifter. The shifter moves the
 * spectral envelope without touching the harmonics, so this changes the apparent size of the
 * singer and leaves the note exactly where it was.
 */
function instrumentFormantShift(instrument: Instrument): number {
  return instrument === 'voxOh' ? -2 : 0;
}

/** The notes this instrument is rendered at. */
export function instrumentNotes(instrument: Instrument): number[] {
  return chromatic(instrumentLowestNote(instrument), NOTES_PER_INSTRUMENT);
}

// MARK: - Patches

function instrumentPatch(instrument: Instrument, frequency: number, seed: bigint): SynthPatch {
  const base = { name: instrumentDisplayName(instrument), symbolName: instrumentSymbol(instrument), seed };

  switch (instrument) {
    case 'electricPiano':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1.0 }),
          // The tine. A triangle an octave up decays with the filter and is the entire
          // difference between "electric piano" and "organ".
          makeSynthLayer({ waveform: 'triangle', semitones: 12, level: 0.42 }),
          makeSynthLayer({ waveform: 'sine', semitones: 19, level: 0.1 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.004,
          hold: 0.012,
          decay: 0.85,
          sustain: 0.22,
          release: 0.35,
          curve: 0.5,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 1700,
          resonance: 0.9,
          envelopeAmount: 1.7,
          envelope: makeSynthEnvelope({ attack: 0.003, decay: 0.3, sustain: 0.12, release: 0.25, curve: 0.7 }),
          keyTracking: 0.6,
        }),
        baseFrequency: frequency,
        duration: 1.6,
        drive: 0.12,
        chain: makeEffectChain([
          makeEffectNode('reverb', { values: { size: 0.35, damping: 0.55, preDelay: 0.008, mix: 0.14 } }),
        ]),
      });

    case 'warmPad':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'saw', level: 0.9, unisonVoices: 4, unisonDetuneCents: 14 }),
          makeSynthLayer({ waveform: 'triangle', semitones: 12, level: 0.32 }),
          makeSynthLayer({ waveform: 'sine', semitones: 19, level: 0.12 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.35, hold: 0.1, decay: 0.8, sustain: 0.72, release: 0.7, curve: -0.3 }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 950,
          resonance: 1.4,
          envelopeAmount: 1.8,
          envelope: makeSynthEnvelope({ attack: 0.5, decay: 1.0, sustain: 0.55, release: 0.6, curve: -0.2 }),
          keyTracking: 0.5,
        }),
        // Filter movement, not pitch movement. A pitch LFO on a pad is vibrato that fights
        // every other pad in the key; a filter LFO is just breathing.
        lfo: makeSynthLFO({ isEnabled: true, waveform: 'sine', rateHz: 0.22, depth: 0.13, target: 'filter' }),
        baseFrequency: frequency,
        duration: 3.0,
        drive: 0.1,
        chain: makeEffectChain([
          makeEffectNode('reverb', { values: { size: 0.62, damping: 0.45, preDelay: 0.02, mix: 0.26 } }),
        ]),
      });

    case 'pluck':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'saw', level: 0.9, unisonVoices: 2, unisonDetuneCents: 8 }),
          makeSynthLayer({ waveform: 'square', semitones: 12, level: 0.24 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.002,
          hold: 0.004,
          decay: 0.35,
          sustain: 0.04,
          release: 0.35,
          curve: 0.75,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 2400,
          resonance: 3.0,
          envelopeAmount: 2.6,
          envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.11, sustain: 0, release: 0.1, curve: 0.85 }),
          keyTracking: 0.7,
        }),
        // Six milliseconds of upward blip. Long enough to hear as a finger catching the
        // string, far too short to affect the sounding pitch.
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 0.6, decay: 0.006, curve: 0.8 }),
        baseFrequency: frequency,
        duration: 1.05,
        drive: 0.16,
        chain: makeEffectChain([
          makeEffectNode('delay', { values: { time: 0.115, feedback: 0.26, tone: 4200, sync: 0, mix: 0.17 } }),
        ]),
      });

    case 'bell':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1.0 }),
          makeSynthLayer({ waveform: 'sine', semitones: 12, level: 0.5 }),
          makeSynthLayer({ waveform: 'sine', semitones: 19, level: 0.26 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.001,
          hold: 0.002,
          decay: 1.3,
          sustain: 0.05,
          release: 0.55,
          curve: 0.8,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 5200,
          resonance: 0.8,
          envelopeAmount: 1.1,
          envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.25, sustain: 0, release: 0.2, curve: 0.8 }),
          keyTracking: 0.8,
        }),
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 1.8, decay: 0.008, curve: 0.85 }),
        baseFrequency: frequency,
        duration: 2.0,
        drive: 0.06,
        chain: makeEffectChain([
          makeEffectNode('reverb', { values: { size: 0.7, damping: 0.35, preDelay: 0.015, mix: 0.3 } }),
        ]),
      });

    case 'marimba':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1.0 }),
          // A tuned bar's first overtone sits two octaves up, not one. That 4:1 ratio is what
          // makes a marimba sound hollow instead of like a flute.
          makeSynthLayer({ waveform: 'sine', semitones: 24, level: 0.34 }),
          makeSynthLayer({ waveform: 'noise', level: 0.09 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0015,
          hold: 0.003,
          decay: 0.4,
          sustain: 0.02,
          release: 0.18,
          curve: 0.8,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 3000,
          resonance: 1.0,
          envelopeAmount: 2.2,
          envelope: makeSynthEnvelope({ attack: 0.0008, decay: 0.03, sustain: 0, release: 0.02, curve: 0.85 }),
          keyTracking: 0.8,
        }),
        baseFrequency: frequency,
        duration: 0.9,
        drive: 0.08,
      });

    case 'organ':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1.0 }),
          makeSynthLayer({ waveform: 'sine', semitones: 12, level: 0.55 }),
          makeSynthLayer({ waveform: 'sine', semitones: 19, level: 0.3 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.008, hold: 0.02, decay: 0.05, sustain: 0.95, release: 0.06, curve: 0 }),
        filter: makeSynthFilter({ type: 'lowPass', cutoff: 4200, resonance: 0.8, envelopeAmount: 0, keyTracking: 0.4 }),
        // Rotary speaker, cheaply: amplitude only, so the pitch never moves.
        lfo: makeSynthLFO({ isEnabled: true, waveform: 'sine', rateHz: 5.6, depth: 0.16, target: 'amplitude' }),
        baseFrequency: frequency,
        duration: 1.4,
        drive: 0.2,
      });

    case 'sawLead':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'saw', level: 0.95, unisonVoices: 3, unisonDetuneCents: 16 }),
          makeSynthLayer({ waveform: 'saw', semitones: 12, level: 0.2 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.006, hold: 0.03, decay: 0.4, sustain: 0.55, release: 0.25, curve: 0.35 }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 1700,
          resonance: 3.2,
          envelopeAmount: 2.4,
          envelope: makeSynthEnvelope({ attack: 0.004, decay: 0.3, sustain: 0.25, release: 0.2, curve: 0.7 }),
          keyTracking: 0.6,
        }),
        baseFrequency: frequency,
        duration: 1.2,
        drive: 0.25,
      });

    case 'squareLead':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'square', level: 1.0 }),
          makeSynthLayer({ waveform: 'saw', level: 0.18 }),
          makeSynthLayer({ waveform: 'square', semitones: 12, level: 0.2 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.004, hold: 0.02, decay: 0.3, sustain: 0.6, release: 0.2, curve: 0.3 }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 2500,
          resonance: 2.0,
          envelopeAmount: 1.8,
          envelope: makeSynthEnvelope({ attack: 0.003, decay: 0.25, sustain: 0.3, release: 0.15, curve: 0.7 }),
          keyTracking: 0.6,
        }),
        baseFrequency: frequency,
        duration: 1.1,
        drive: 0.18,
      });

    case 'brassStab':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'saw', level: 0.92, unisonVoices: 3, unisonDetuneCents: 10 }),
          makeSynthLayer({ waveform: 'square', level: 0.2 }),
          makeSynthLayer({ waveform: 'saw', semitones: 12, level: 0.22 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.02, hold: 0.03, decay: 0.25, sustain: 0.5, release: 0.18, curve: 0.2 }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 1150,
          resonance: 1.6,
          // The 35 ms filter attack *is* the brass. A player's lips take that long to reach
          // full buzz, and a stab without it sounds like a pad.
          envelopeAmount: 2.8,
          envelope: makeSynthEnvelope({ attack: 0.035, decay: 0.35, sustain: 0.32, release: 0.15, curve: 0.4 }),
          keyTracking: 0.5,
        }),
        // Thirty milliseconds of scoop up into the note, the way a horn section arrives
        // slightly under the pitch and settles onto it.
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: -0.7, decay: 0.03, curve: -0.2 }),
        baseFrequency: frequency,
        duration: 0.95,
        drive: 0.3,
      });

    case 'stringSwell':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'saw', level: 0.9, unisonVoices: 5, unisonDetuneCents: 22 }),
          makeSynthLayer({ waveform: 'saw', semitones: 12, level: 0.24, unisonVoices: 2, unisonDetuneCents: 10 }),
          makeSynthLayer({ waveform: 'triangle', semitones: 19, level: 0.1 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.9, hold: 0.2, decay: 0.6, sustain: 0.75, release: 0.9, curve: -0.5 }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 1400,
          resonance: 1.0,
          envelopeAmount: 1.6,
          envelope: makeSynthEnvelope({ attack: 1.1, decay: 0.8, sustain: 0.7, release: 0.8, curve: -0.3 }),
          keyTracking: 0.4,
        }),
        baseFrequency: frequency,
        duration: 3.4,
        drive: 0.08,
        chain: makeEffectChain([
          makeEffectNode('reverb', { values: { size: 0.75, damping: 0.4, preDelay: 0.025, mix: 0.32 } }),
        ]),
      });

    case 'voxAh':
    case 'voxOh':
      return makeSynthPatch({
        ...base,
        layers: [
          // The glottal source. Two barely-detuned saws sound like one voice that is not quite
          // steady; more than that sounds like a choir.
          makeSynthLayer({ waveform: 'saw', level: 1.0, unisonVoices: 2, unisonDetuneCents: 6 }),
          makeSynthLayer({ waveform: 'triangle', semitones: 12, level: 0.14 }),
        ],
        amplitude: makeSynthEnvelope({ attack: 0.22, hold: 0.1, decay: 0.5, sustain: 0.82, release: 0.5, curve: -0.2 }),
        filter: makeSynthFilter({ type: 'lowPass', cutoff: 4000, resonance: 0.7, envelopeAmount: 0, keyTracking: 0.3 }),
        // Five-hertz vibrato at five cents of depth. Frequency modulation leaves the carrier
        // exactly where it was — the note stays in tune to the cent while sounding human
        // rather than held by a machine.
        lfo: makeSynthLFO({ isEnabled: true, waveform: 'sine', rateHz: 5.0, depth: 0.004, target: 'pitch' }),
        baseFrequency: frequency,
        duration: 2.4,
        drive: 0.1,
      });
  }
}

/** Renders one note of one instrument, finished and levelled. */
export async function renderInstrumentNote(
  instrument: Instrument,
  midi: number,
  context: LibraryRenderContext,
): Promise<RenderedAudio> {
  const sampleRate = context.sampleRate;
  const budget = context.budget;
  // Seeded from the instrument and the note so two noise layers in the same kit never render
  // the identical hiss, while every render stays reproducible.
  const seed = librarySeed(`melodic-${instrumentToken(instrument)}`, midi);

  const audio = await renderPatch(instrumentPatch(instrument, noteFrequency(midi), seed), {
    sampleRate,
    ...(budget === undefined ? {} : { budget }),
  });
  let channels = audio.channels;

  const vowel = instrumentVowel(instrument);
  if (vowel !== null) {
    applyFormantBank(channels, vowel, sampleRate);
    const shift = instrumentFormantShift(instrument);
    if (shift !== 0) {
      channels = await applyEffectNode(channels, makeEffectNode('formantShift', { values: { shift } }), {
        sampleRate,
        tempo: 120,
        ...(budget === undefined ? {} : { budget }),
      });
    }
  }

  finishToRMS(channels, instrumentTargetRMSDB(instrument));
  return { channels, sampleRate };
}

function instrumentEntries(instrument: Instrument): LibraryEntry[] {
  return instrumentNotes(instrument).map((midi) =>
    makeLibraryEntry({
      id: `melodic-${instrumentToken(instrument)}-${noteSlug(midi)}`,
      fileName: `melodic-${instrumentToken(instrument)}-${noteFileToken(midi)}.wav`,
      name: `${instrumentDisplayName(instrument)} ${noteName(midi)}`,
      caption: instrumentCaption(instrument),
      tags: instrumentTags(instrument),
      family: 'melodic',
      group: instrumentDisplayName(instrument),
      color: instrumentColor(instrument),
      symbolName: instrumentSymbol(instrument),
      rootMidi: midi,
      render: (context) => renderInstrumentNote(instrument, midi, context),
    }),
  );
}

// MARK: - Chord stabs

export type ChordQuality = 'major' | 'minor' | 'minor7' | 'major7' | 'sus4';

export const CHORD_QUALITIES: readonly ChordQuality[] = ['major', 'minor', 'minor7', 'major7', 'sus4'];

/** Chord symbol suffix, so the pad reads "Cm7" rather than "C Minor 7". */
function chordSuffix(quality: ChordQuality): string {
  switch (quality) {
    case 'major':
      return '';
    case 'minor':
      return 'm';
    case 'minor7':
      return 'm7';
    case 'major7':
      return 'maj7';
    case 'sus4':
      return 'sus4';
  }
}

/**
 * Semitones above the root. Voiced close, in root position — a wider voicing sounds better on
 * its own and muddier the moment a bass line is under it, and these exist to be stacked on a
 * bass line.
 */
export function chordIntervals(quality: ChordQuality): number[] {
  switch (quality) {
    case 'major':
      return [0, 4, 7];
    case 'minor':
      return [0, 3, 7];
    case 'minor7':
      return [0, 3, 7, 10];
    case 'major7':
      return [0, 4, 7, 11];
    case 'sus4':
      return [0, 5, 7];
  }
}

function chordCaption(quality: ChordQuality): string {
  switch (quality) {
    case 'major':
      return 'Major chord stab. Bright and resolved.';
    case 'minor':
      return 'Minor chord stab. The default colour of most modern beats.';
    case 'minor7':
      return 'Minor seventh stab. Minor with the tension a lo-fi or neo-soul loop lives on.';
    case 'major7':
      return 'Major seventh stab. Warm and open, one step short of sweet.';
    case 'sus4':
      return 'Suspended fourth stab. Unresolved on purpose. It wants to move somewhere.';
  }
}

/**
 * Keys the chords are rendered in.
 *
 * C, F and G are the I–IV–V of C major: three stabs from this set already make a progression,
 * which is far more useful than three unrelated keys would be.
 */
export const CHORD_ROOTS = [48, 53, 55]; // C3, F3, G3

export const CHORD_TARGET_RMS_DB = -17;

/**
 * The voice each chord tone is rendered with. A saw-and-square stack through a medium-fast
 * filter: enough attack to punch, enough sustain to hold a bar.
 */
function chordVoicePatch(frequency: number, seed: bigint): SynthPatch {
  return makeSynthPatch({
    name: 'Chord Voice',
    symbolName: 'music.note',
    layers: [
      makeSynthLayer({ waveform: 'saw', level: 0.9, unisonVoices: 2, unisonDetuneCents: 9 }),
      makeSynthLayer({ waveform: 'square', level: 0.28 }),
      makeSynthLayer({ waveform: 'sine', semitones: 12, level: 0.16 }),
    ],
    amplitude: makeSynthEnvelope({ attack: 0.008, hold: 0.02, decay: 0.55, sustain: 0.35, release: 0.45, curve: 0.45 }),
    filter: makeSynthFilter({
      type: 'lowPass',
      cutoff: 1500,
      resonance: 1.8,
      envelopeAmount: 2.2,
      envelope: makeSynthEnvelope({ attack: 0.006, decay: 0.28, sustain: 0.22, release: 0.3, curve: 0.6 }),
      keyTracking: 0.6,
    }),
    baseFrequency: frequency,
    duration: 1.5,
    drive: 0.18,
    seed,
  });
}

/**
 * Renders a chord as real stacked intervals: one full synth voice per chord tone, mixed, then
 * glued with a compressor and a short room.
 *
 * Rendering the tones separately rather than as layers of one patch is not a detail — a patch
 * allows three layers and a seventh chord needs four voices, each with its own filter envelope
 * and its own place in the stereo image. That is what stops a four-note stack from sounding
 * like one buzzing oscillator.
 */
export async function renderChord(
  rootMidi: number,
  quality: ChordQuality,
  context: LibraryRenderContext,
): Promise<RenderedAudio> {
  const sampleRate = context.sampleRate;
  const budget = context.budget;
  const intervals = chordIntervals(quality);
  const frames = Math.max(1, Math.round(1.5 * sampleRate));
  const mix = [new Float32Array(frames), new Float32Array(frames)];

  // Equal-power across the stack, and the root a little louder than the colour tones, so a
  // seventh chord is not automatically quieter — or muddier — than a triad.
  const voiceGain = 1 / Math.sqrt(intervals.length);

  for (let index = 0; index < intervals.length; index++) {
    const interval = intervals[index]!;
    const frequency = noteFrequency(rootMidi) * Math.pow(2, interval / 12);
    const voice = await renderPatch(chordVoicePatch(frequency, librarySeed(`chord-${quality}-${rootMidi}`, interval)), {
      sampleRate,
      ...(budget === undefined ? {} : { budget }),
    });
    // Root centred, upper tones fanned outward in alternating directions. Keeps the chord's
    // centre of gravity in the middle while giving it width.
    const pan = index === 0 ? 0 : (index % 2 === 1 ? -1 : 1) * Math.min(0.34, 0.13 * index);
    mixInto(mix, voice.channels, 0, index === 0 ? voiceGain : voiceGain * 0.84, pan);
  }

  const bus = makeEffectChain([
    makeEffectNode('compressor', { values: { threshold: -16, ratio: 3.0, attack: 0.008, release: 0.16, makeup: 2.5 } }),
    makeEffectNode('reverb', { values: { size: 0.42, damping: 0.5, preDelay: 0.012, mix: 0.18 } }),
  ]);
  const processed = await applyEffectChain(mix, bus, {
    sampleRate,
    tempo: 120,
    ...(budget === undefined ? {} : { budget }),
  });

  finishToRMS(processed, CHORD_TARGET_RMS_DB);
  return { channels: processed, sampleRate };
}

function chordEntries(): LibraryEntry[] {
  const entries: LibraryEntry[] = [];
  for (const root of CHORD_ROOTS) {
    for (const quality of CHORD_QUALITIES) {
      const symbol = notePitchClass(root) + chordSuffix(quality);
      entries.push(
        makeLibraryEntry({
          id: `chord-${quality}-${noteSlug(root)}`,
          fileName: `chord-${quality}-${noteFileToken(root)}.wav`,
          name: `${symbol} Stab`,
          caption: `${chordCaption(quality)} Rendered as ${chordIntervals(quality).length} stacked voices from ${noteName(root)}.`,
          tags: ['melodic', 'chord', 'stab', quality, `key:${notePitchClass(root)}`],
          family: 'chords',
          group: 'Chord Stabs',
          color: 'indigo',
          symbolName: 'music.note.list',
          rootMidi: root,
          render: (renderContext) => renderChord(root, quality, renderContext),
        }),
      );
    }
  }
  return entries;
}

// MARK: - Catalogue

/** Twelve instruments × thirteen chromatic notes. */
export const melodicEntries: readonly LibraryEntry[] = INSTRUMENTS.flatMap(instrumentEntries);

/** Five qualities in three keys. */
export const chordEntriesList: readonly LibraryEntry[] = chordEntries();
